import React, { useState, useEffect } from "react";
import { QuestionType, QuizListType } from "utils/interfaces";
import StartQuizView from "./utils/StartQuizView";
import QuizResponse from "./utils/QuizResponse";
import { QuizProgress } from "routes/quiz/Quiz";
import {
  MdCheckCircle,
  MdInfo,
  MdOutlineArrowCircleLeft,
  MdOutlineArrowCircleRight,
} from "react-icons/md";
import { checkQuizAnswer, updateResults } from "services/resultsRequests";

interface TakeQuizProps {
  quiz: QuizListType;
  savedProgress: QuizProgress | null;
  onProgressUpdate?: (progress: Omit<QuizProgress, "timestamp">) => void;
  exitQuiz: () => void;
  // onBack: () => void;
  // onContinue: () => void;
  // onSubmit: () => void;
  // onRestart: () => void;
  onComplete?: () => void;
}

const TakeQuiz: React.FC<TakeQuizProps> = ({
  quiz,
  savedProgress,
  onProgressUpdate,
  onComplete,
  exitQuiz,
  // onBack,
  // onContinue,
  // onSubmit,
  // onRestart,
}) => {
  // States definition
  const [selectedOption, setSelectedOption] = useState<number | undefined>(
    undefined,
  );
  const [answeredCorrectly, setAnsweredCorrectly] = useState<
    boolean | undefined
  >(undefined);
  const [answerHistory, setAnswerHistory] = useState<number[][]>([[]]); // where answerHistory[0] is first answer, answerHistory[0][...] is incorrect answers
  const [reviewingPerformance, setReviewingPerformance] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(-1);
  const [correctAnswers, setCorrectAnswers] = useState<Record<number, number>>(
    {},
  ); // Track which answer was correct for each question

  const [isChecking, setIsChecking] = useState(false); // used to temporarily disable "Check Answer" button to prevent duplicate requests

  // Load quiz progress when on mount
  useEffect(() => {
    if (savedProgress && savedProgress.quizId === quiz._id) {
      setCurrentQuestion(savedProgress.currentQuestionIndex);

      // Reconstruct answer history from saved answers
      const reconstructedHistory: number[][] = [];
      for (let i = 0; i <= savedProgress.currentQuestionIndex; i++) {
        if (savedProgress.answers[i]) {
          reconstructedHistory.push(savedProgress.answers[i]);
        } else {
          reconstructedHistory.push([]);
        }
      }
      // Add empty array for current question if needed
      if (reconstructedHistory.length === savedProgress.currentQuestionIndex) {
        reconstructedHistory.push([]);
      }

      setAnswerHistory(reconstructedHistory);

      if (savedProgress.correctAnswers) {
        setCorrectAnswers(savedProgress.correctAnswers);
      }

      const currentQuestionHistory =
        reconstructedHistory[savedProgress.currentQuestionIndex];
      const isLastInHistory =
        savedProgress.currentQuestionIndex === reconstructedHistory.length - 1;

      if (
        currentQuestionHistory &&
        currentQuestionHistory.length === 0 &&
        !isLastInHistory
      ) {
        // This question was answered correctly before
        setAnsweredCorrectly(true);
        setSelectedOption(
          savedProgress.correctAnswers?.[savedProgress.currentQuestionIndex] ??
            getQuestionCorrectIndex(savedProgress.currentQuestionIndex),
        );
      } else {
        // This question hasn't been answered correctly yet
        setAnsweredCorrectly(undefined);
        setSelectedOption(undefined);
      }
    }
  }, [savedProgress]);

  // Save progress whenever current question or answer history changes
  useEffect(() => {
    if (currentQuestion >= 0 && !reviewingPerformance && onProgressUpdate) {
      const answersRecord: Record<number, number[]> = {};
      answerHistory.forEach((answers, index) => {
        answersRecord[index] = answers;
      });

      onProgressUpdate({
        quizId: quiz._id,
        currentQuestionIndex: currentQuestion,
        answers: answersRecord,
        correctAnswers: correctAnswers,
      });
    }
  }, [currentQuestion, answerHistory, reviewingPerformance]);

  const thisQuestion = (questionIndex: number) => quiz.questions[questionIndex];

  const isCorrectAnswer = (questionIndex: number, responseIndex: number) => {
    const _thisQuestion = thisQuestion(questionIndex);
    if (!_thisQuestion || !_thisQuestion.responses) return false;
    if (responseIndex >= _thisQuestion.responses.length) return false;
    return _thisQuestion.responses.map((response) => response.correct)[
      responseIndex
    ];
  };

  const getQuestionCorrectIndex = (questionIndex: number) => {
    const _thisQuestion = thisQuestion(questionIndex);
    if (!_thisQuestion || !_thisQuestion.responses) return undefined;
    const out = _thisQuestion.responses
      .map((response) => response.correct)
      .indexOf(true);
    return out >= 0 ? out : undefined;
  };

  const checkAnswer = async () => {
    if (isChecking) return; // Prevent multiple calls while checking

    try {
      setIsChecking(true); // Set loading state

      const quizId = quiz._id;
      const questionText = quiz.questions[currentQuestion].title;
      const selectedText =
        quiz.questions[currentQuestion].responses[selectedOption || 0].text;
      const apiAnswer = await checkQuizAnswer(
        quizId,
        questionText,
        selectedText,
      );

      if (
        selectedOption === undefined ||
        selectedOption < 0 ||
        selectedOption >= quiz.questions[currentQuestion].responses.length
      )
        return;

      // Add the 'correct' field to the quiz question responses based on API answer
      // This is important for the performance review section now that the fetched quiz hides this info by default
      quiz.questions[currentQuestion].responses = quiz.questions[
        currentQuestion
      ].responses.map((response, index) => ({
        ...response,
        correct: index === selectedOption ? apiAnswer.correct : undefined,
      }));

      if (apiAnswer.correct) {
        setAnsweredCorrectly(true);
        setAnswerHistory((history) => [...history, []]);
        setCorrectAnswers((prev) => ({
          ...prev,
          [currentQuestion]: selectedOption,
        }));

        if (
          (quiz.questions ? quiz.questions.length : 0) ===
          currentQuestion + 1
        ) {
          submitAnswers();
        }
      } else {
        setAnswerHistory((history: number[][]) => {
          if (history.length === currentQuestion || history.length === 0) {
            return [...history, [selectedOption]];
          }
          let current: number[] = history.pop() || [];
          if (current) {
            current.push(selectedOption);
            history.push(current);
            return history;
          }
          return [[]];
        });
        setSelectedOption(undefined);
        setAnsweredCorrectly(false);
      }
    } catch (error) {
      setAnsweredCorrectly(false);
    } finally {
      setIsChecking(false); // Reset loading state
    }
  };

  const nextQuestion = () => {
    // Check if next question was answered correctly before
    // If next question is NOT the last question in answerHistory, user must've gotten it correct
    if (currentQuestion + 1 < answerHistory.length - 1) {
      setAnsweredCorrectly(true);

      const savedCorrectAnswer = correctAnswers[currentQuestion + 1];
      setSelectedOption(
        savedCorrectAnswer ?? getQuestionCorrectIndex(currentQuestion + 1),
      );
    } else {
      setSelectedOption(undefined);
      setAnsweredCorrectly(undefined);
    }
    setCurrentQuestion((value) => value + 1);
  };

  const startQuiz = () => {
    updateResults("quizzes", quiz._id, "ongoing");
    setCurrentQuestion(0);
  };

  const previousQuestion = () => {
    setCurrentQuestion((value) => value - 1);
    setAnsweredCorrectly(true);
    const savedCorrectAnswer = correctAnswers[currentQuestion - 1];
    setSelectedOption(
      savedCorrectAnswer ?? getQuestionCorrectIndex(currentQuestion - 1),
    );
  };

  const submitAnswers = () => {
    // todo: post data to backend....
  };

  const retakeQuiz = () => {
    setAnsweredCorrectly(undefined);
    setSelectedOption(undefined);
    setAnswerHistory([[]]);
    setCorrectAnswers({});
    setReviewingPerformance(false);
    setCurrentQuestion(0);

    if (onComplete) {
      onComplete();
    }
  };

  const renderQuestion = (
    questionId: number,
    quizComplete: boolean = false,
  ) => {
    if (
      questionId < 0 ||
      questionId >= (quiz.questions ? quiz.questions.length : 0)
    )
      return <></>;
    const question: QuestionType = quiz.questions[questionId];
    return (
      <>
        {/* Title */}
        <div className="w-[542px] h-fit flex-col justify-start items-start gap-1 flex">
          <div className="text-neutral-500 text-xs font-normal uppercase leading-[14px] tracking-wide">
            Question {questionId + 1} of{" "}
            {quiz.questions ? quiz.questions.length : 0}
          </div>
          <div className="text-neutral-600 text-lg font-normal leading-snug">
            {question.title}
          </div>
        </div>

        {/* Question and buttons */}
        <div
          className={`w-full h-full flex-col justify-start items-center gap-10 flex${
            quizComplete ? "" : " overflow-y-auto custom-scroll"
          }`}
        >
          {/* Questions */}
          <div className="w-full h-fit flex-col justify-start items-center gap-4 flex ">
            {question.responses.map((response, index) => {
              return (
                <QuizResponse
                  key={index}
                  index={index}
                  text={response.text}
                  status={
                    quizComplete
                      ? isCorrectAnswer(questionId, index)
                        ? "correct"
                        : answerHistory[questionId].includes(index)
                          ? "incorrect"
                          : correctAnswers[questionId] === index
                            ? "correct"
                            : "grey-disabled"
                      : selectedOption === index
                        ? "selected"
                        : answerHistory[questionId].includes(index)
                          ? "grey-disabled"
                          : currentQuestion === answerHistory.length - 1
                            ? "unselected"
                            : "disabled"
                  }
                  onClick={setSelectedOption}
                />
              );
            })}
          </div>
        </div>
      </>
    );
  };

  const numCorrect = () =>
    answerHistory.filter((response: number[]) => response.length === 0).length -
    1;

  return (
    <>
      {currentQuestion === -1 ? (
        <StartQuizView
          quiz={quiz}
          onStartClick={startQuiz}
          onBackClick={exitQuiz}
        />
      ) : !reviewingPerformance ? (
        <div className="w-full h-full flex-col justify-start items-center gap-10 flex overflow-hidden">
          {/* pop up message*/}
          {answeredCorrectly === true ? (
            <div className="h-fit p-3 bg-success-100 rounded-lg justify-start items-center gap-3 inline-flex">
              <MdCheckCircle className="w-8 h-8 text-neutral-700" />
              <div className="w-[474px] text-neutral-700 text-base font-semibold leading-[19px]">
                Good job!
              </div>
            </div>
          ) : (
            answeredCorrectly === false && (
              <div className="h-fit p-3 bg-error-100 rounded-lg justify-start items-center gap-3 inline-flex">
                <MdInfo className="w-8 h-8 text-neutral-700" />
                <div className="w-[474px] text-neutral-700 text-base font-semibold leading-[19px]">
                  Incorrect answer. Please try again.
                </div>
              </div>
            )
          )}
          {/* Quiz question */}
          <div className="w-full h-full flex-col justify-between items-center gap-7 inline-flex z-50 overflow-hidden">
            {renderQuestion(currentQuestion)}
            {/* Navigation & buttons */}
            <div className="w-[542px] h-fit justify-between items-center inline-flex">
              {currentQuestion > 0 && (
                <div
                  className="rounded justify-center items-center gap-2 flex select-none cursor-pointer"
                  onClick={previousQuestion}
                >
                  <MdOutlineArrowCircleLeft className="w-6 h-6 text-secondary-600" />
                  <div className="text-secondary-600 text-lg font-semibold leading-snug">
                    Prev
                  </div>
                </div>
              )}
              {answeredCorrectly === true &&
                (currentQuestion <
                (quiz.questions ? quiz.questions.length : 0) - 1 ? (
                  <div
                    className="h-[46px] px-0 py-3 rounded justify-center items-center gap-2 inline-flex ml-auto mr-0 select-none cursor-pointer"
                    onClick={nextQuestion}
                  >
                    <div className="text-secondary-600 text-lg font-semibold leading-snug">
                      Next
                    </div>
                    <MdOutlineArrowCircleRight className="w-6 h-6 text-secondary-600" />
                  </div>
                ) : (
                  <div
                    className="h-[46px] px-5 py-3 bg-secondary-600 hover:bg-secondary-700 ease-in-out duration-150 rounded justify-center items-center gap-2 inline-flex ml-auto mr-0 select-none cursor-pointer"
                    onClick={() => setReviewingPerformance(true)}
                  >
                    <div className="text-white text-lg font-semibold font-poppins leading-snug select-none">
                      Review my performance
                    </div>
                  </div>
                ))}
              {answeredCorrectly !== true && (
                <div
                  className={`h-[46px] px-5 py-3 rounded justify-center items-center gap-2 inline-flex ml-auto mr-0 select-none ${
                    selectedOption === undefined
                      ? "bg-Subtle cursor-not-allowed"
                      : "bg-secondary-600 hover:bg-secondary-700 ease-in-out duration-150 cursor-pointer"
                  }`}
                  onClick={() => selectedOption !== undefined && checkAnswer()}
                >
                  <div
                    className={`text-lg font-semibold leading-snug ${
                      selectedOption === undefined
                        ? "text-neutral-500"
                        : "text-white"
                    }`}
                  >
                    Check answer
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        // Review answers after finished with quiz
        <div className="w-full h-[calc(100vh-172px)] flex-1 flex flex-col mx-auto justify-center items-center gap-12 font-poppins">
          {/* "subheader" */}
          <div className="flex-col justify-start items-center gap-6 inline-flex">
            <div className="text-center text-neutral-600 text-lg font-semibold font-poppins leading-snug">
              You answered {numCorrect()} out of{" "}
              {quiz.questions ? quiz.questions.length : 0} correctly
            </div>
            <div>
              <p className="text-neutral-700">
                You can retake this quiz for revision, but the results won't
                count towards your aptitude
              </p>
            </div>
            <div className="justify-start items-center gap-6 inline-flex">
              <div className="h-[46px] justify-start items-center gap-6 inline-flex">
                {/* retake quiz */}
                <div
                  className="px-5 py-3 rounded justify-center items-center gap-2 inline-flex cursor-pointer"
                  onClick={retakeQuiz}
                >
                  <div className="text-secondary-600 hover:underline text-lg font-semibold font-poppins leading-snug select-none">
                    Retake Quiz
                  </div>
                </div>
                {/* continue course */}
                <div
                  className="px-5 py-3 bg-secondary-600 hover:bg-secondary-700 ease-in-out duration-150 rounded justify-center items-center gap-2 inline-flex cursor-pointer"
                  onClick={exitQuiz}
                >
                  <div className="text-white text-lg font-semibold font-poppins leading-snug">
                    Continue course
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* questions */}
          <div className="py-0 px-8 mb-12 flex-1 overflow-y-auto custom-scroll flex-col justify-start items-start gap-10 flex">
            {answerHistory.map((wrongAnswers: number[], index: number) =>
              renderQuestion(index, true),
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default TakeQuiz;
